/**
 * Sentry Event Tracking Utilities
 * 
 * Centralized helpers for tracking iProov events with Sentry.
 * Uses Sentry.withScope to add tags, context, and capture messages/exceptions.
 */

import * as Sentry from "@sentry/react";
import {
  collectDeviceInfo,
  getFrameDepth,
  getIPhoneBucket,
  getMotionSupport,
  isDesktopModeSpoofed,
  type DeviceInfo,
} from "./deviceInfo";
import type { IProovSupportCheck } from "./iproovSupport";

/**
 * Tag this frame's motion capability onto a scope (VEX-7631). Synchronous and
 * prompt-free, so it is safe on every iProov error path.
 *
 * Answers the question our motion telemetry could not: when iProov reports motion
 * unsupported or denied, was this frame even permitted to collect motion? The
 * `frame-` prefix follows the existing frame-scoped tags — these describe THIS
 * iframe, not the member's device or their site-level grant.
 *
 * Read `motion-api` first, since it says which of the two branches of iProov's test
 * ran. The policy tags are Chromium-only and read "unsupported" on iOS. See
 * getMotionSupport() for the full semantics.
 */
function tagMotionSupport(scope: Sentry.Scope): void {
  const support = getMotionSupport();
  scope.setTag("frame-depth", String(getFrameDepth() ?? "unknown"));
  scope.setTag("motion-api", support.motionApi);
  scope.setTag("frame-policy-accelerometer", support.policyAccelerometer);
  scope.setTag("frame-policy-gyroscope", support.policyGyroscope);
  scope.setTag("frame-policy-magnetometer", support.policyMagnetometer);
  tagDeviceClass(scope);
}

/**
 * VEX-7617. `iphone-bucket` tests whether iOS motion failures are a real device limit
 * (every iPhone since the 4S has the sensors, so a flat split indicts the Settings toggle);
 * `desktop-mode` un-contaminates desktop cohorts. Low cardinality on purpose.
 */
function tagDeviceClass(scope: Sentry.Scope): void {
  scope.setTag("iphone-bucket", getIPhoneBucket());
  scope.setTag("desktop-mode", String(isDesktopModeSpoofed()));
}

/** Sentry truncates tag values at 200 characters; do it here so the cut is predictable. */
const MAX_TAG_LENGTH = 200;

/**
 * Every `feedback_code` iProov returned, in their order (up to three since v6).
 */
function iproovFeedbackCodes(eventDetail: any): string[] {
  const reasons = eventDetail?.reasons;
  if (!Array.isArray(reasons)) return [];
  return reasons.map((r: any) => r?.feedback_code).filter(Boolean);
}

/**
 * Flatten `reasons` to one string per reason before it becomes Sentry context.
 *
 * Sentry normalizes context to a depth of 3, and `reasons` is an array of objects
 * *inside* the context object — one level too deep — so it arrived as
 * "[Object], [Object], [Object]" and the codes were unreadable in the UI. Flattening
 * here keeps them, rather than raising `normalizeDepth` globally and inflating every
 * payload for one field.
 */
function flattenIProovDetail(eventDetail: any): Record<string, unknown> {
  if (!eventDetail || typeof eventDetail !== "object") return { detail: eventDetail };
  const { reasons, ...rest } = eventDetail as Record<string, unknown>;
  if (!Array.isArray(reasons)) return eventDetail as Record<string, unknown>;
  return {
    ...rest,
    reasons: reasons.map((r: any) =>
      [r?.feedback_code, r?.localized_description].filter(Boolean).join(": ") || String(r)
    ),
  };
}

/**
 * All the codes as one searchable tag.
 *
 * `liveness-feedback` stays `reasons[0]` — it drives fingerprinting and issue grouping,
 * which must not move (VEX-7653). But iProov order by proximity to the cause, not by how
 * actionable the fix is, so the first code is not always the useful one: a laptop with the
 * screen dimmed to zero returns `device_issue` first and `lighting_issues` second. Context
 * is not searchable in Sentry, so without this tag there is no way to ask how often a code
 * appears anywhere in the list rather than first.
 */
function tagFeedbackCodes(scope: Sentry.Scope, eventDetail: any): void {
  const codes = iproovFeedbackCodes(eventDetail);
  if (codes.length === 0) return;
  scope.setTag("liveness-feedbacks", codes.join(",").slice(0, MAX_TAG_LENGTH));
}

/**
 * Track iProov failure event.
 *
 * The feedback code is folded into the message (VEX-7631). This is a captureMessage,
 * which Sentry groups by message text, so the previous constant `"iProov Failure:
 * failed"` collapsed every failure mode — too_bright, eyes_closed, too_much_movement,
 * face_not_found — into one issue. These codes are the primary per-attempt pass-rate
 * signal, so they need to be separately trendable.
 *
 * This is also what makes the GPA screen-area question answerable: `too_bright`
 * ("ambient light too strong OR screen brightness too low") is returned only by Genuine
 * Presence Assurance, so it can only be trended per-browser once the codes are split.
 *
 * @param feedback - Feedback from iProov
 * @param reason - Reason for failure
 * @param eventDetail - Full event detail object
 * @param verificationId - Current verification ID
 * @param sessionToken - Session token (checked for presence)
 */
export function trackIProovFailure(
  feedback: string | undefined,
  reason: string | undefined,
  eventDetail: any,
  verificationId: string | null,
  sessionToken: string | null
): void {
  Sentry.withScope((scope) => {
    scope.setTag("liveness-event", "failed");
    scope.setTag("liveness-feedback", feedback || "none");
    scope.setTag("liveness-reason", reason || "unknown");
    tagFeedbackCodes(scope, eventDetail);
    scope.setContext("iproov-event", flattenIProovDetail(eventDetail));
    scope.setContext("verification", {
      verificationId: verificationId || "unknown",
      sessionTokenPresent: !!sessionToken,
    });
    Sentry.captureMessage(`iProov Failure: ${feedback || "unknown"}`, 'warning');
  });
}

/**
 * Track iProov error event
 * 
 * Fingerprinted on the iProov feedback code (VEX-7631). Without it, Sentry groups
 * this by stack trace — and the stack is always these same few lines, so EVERY
 * feedback code collapsed into one issue titled after whichever event happened to
 * arrive first (~3.5K/month spanning error_camera, error_no_face_found,
 * error_device_motion_unsupported, ...). Grouping on the code instead gives one
 * issue per failure mode, which is the unit we actually triage in.
 *
 * @param errorMsg - Error message from iProov
 * @param feedback - Feedback from iProov
 * @param eventDetail - Full event detail object
 * @param verificationId - Current verification ID
 * @param sessionToken - Session token (checked for presence)
 */
export function trackIProovError(
  errorMsg: string | undefined,
  feedback: string | undefined,
  eventDetail: any,
  verificationId: string | null,
  sessionToken: string | null
): void {
  Sentry.withScope((scope) => {
    scope.setFingerprint(["iproov-error", feedback || "unknown"]);
    tagMotionSupport(scope);
    scope.setTag("liveness-event", "error");
    scope.setTag("liveness-feedback", feedback || "none");
    scope.setTag("liveness-error", errorMsg || "unknown");
    tagFeedbackCodes(scope, eventDetail);
    scope.setContext("iproov-event", flattenIProovDetail(eventDetail));
    scope.setContext("verification", {
      verificationId: verificationId || "unknown",
      sessionTokenPresent: !!sessionToken,
    });
    Sentry.captureException(new Error(`iProov Error: ${errorMsg || "iProov error occurred"}`));
  });
}

/**
 * Track iProov cancellation event
 * 
 * @param verificationId - Current verification ID
 * @param sessionToken - Session token (checked for presence)
 */
export function trackIProovCanceled(
  verificationId: string | null,
  sessionToken: string | null
): void {
  Sentry.withScope((scope) => {
    scope.setTag("liveness-event", "canceled");
    scope.setContext("verification", {
      verificationId: verificationId || "unknown",
      sessionTokenPresent: !!sessionToken,
    });
    Sentry.captureMessage(`iProov Canceled: User cancelled verification`, 'info');
  });
}

/**
 * Track iProov unsupported device/browser event.
 *
 * iProov fires `unsupported` for many unrelated reasons (browser below its minimum
 * version, no WASM, no fullscreen, no video input, blocked localStorage, missing
 * accelerometer/gyroscope/magnetometer permissions policy). The reason rides in
 * `event.detail.reasons`, so we tag the code and attach the detail — previously we
 * sent neither and every reason landed in one indistinguishable issue (VEX-7631).
 *
 * The code is also folded into the message: this is a captureMessage, which Sentry
 * groups by message text, so varying it splits the issues per reason. (Contrast
 * trackIProovError, a captureException, which needs an explicit fingerprint because
 * exceptions group by stack trace instead.)
 *
 * @param verificationId - Current verification ID
 * @param sessionToken - Session token (checked for presence)
 * @param feedback - iProov feedback code from reasons[0], where supplied
 * @param eventDetail - Full event detail object
 */
export function trackIProovUnsupported(
  verificationId: string | null,
  sessionToken: string | null,
  feedback?: string,
  eventDetail?: unknown
): void {
  Sentry.withScope((scope) => {
    tagMotionSupport(scope);
    scope.setTag("liveness-event", "unsupported");
    scope.setTag("liveness-feedback", feedback || "none");
    scope.setContext("verification", {
      verificationId: verificationId || "unknown",
      sessionTokenPresent: !!sessionToken,
    });
    // NOT setContext("browser", …) — the same defect VEX-7628 fixed by renaming "device"
    // to "deviceSnapshot". `browser` is one of Sentry's own context keys (name, version),
    // and overwriting it drops the parsed values Discover's browser.name filter reads.
    // That filter is precisely how the Chrome-iOS / Google-app cohort gets sliced, so
    // clobbering it defeats the reason these events are captured.
    scope.setContext("browserSnapshot", {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
    });
    if (eventDetail) {
      scope.setContext("iproov-event", flattenIProovDetail(eventDetail));
    }
    Sentry.captureMessage(
      `iProov Unsupported: ${feedback || "device or browser not supported"}`,
      'error'
    );
  });
}

/**
 * Pre-flight iProovSupport check (VEX-7617). Only called on a decisive negative or when the
 * check couldn't run. Fingerprinted on the first failing test because that's the unit we act
 * on — each needs a different remedy, so they must not share an issue.
 */
export function trackSupportCheck(
  verificationId: string | null,
  sessionToken: string | null,
  result: IProovSupportCheck
): void {
  Sentry.withScope((scope) => {
    const primary = result.ran ? (result.failedTests[0] ?? "unknown") : "did-not-run";

    scope.setFingerprint(["iproov-support-check", primary]);
    tagMotionSupport(scope);
    scope.setTag("liveness-event", "support_check");
    scope.setTag("support-check-ran", String(result.ran));
    scope.setTag("support-failed-test", primary);
    scope.setTag("support-native-bridge", String(result.isNativeBridge));
    // iProov's bundled ua-parser-js; "inapp" covers GSA / Instagram / Facebook / TikTok.
    scope.setTag("support-browser-type", result.browserType ?? "unknown");
    scope.setTag("support-device-type", result.deviceType ?? "unknown");
    // Ours (VEX-7682) — finer-grained; support-browser-type collapses all in-app to "inapp".
    scope.setTag("support-browser-family", result.browserFamily);
    scope.setTag("support-reload-can-reprompt", String(result.reloadCanRePrompt));
    scope.setTag("support-in-iframe", String(result.inIframe ?? "unknown"));
    // Prompt-free, so unlike `motion-api` it distinguishes a denial from a missing API.
    scope.setTag("support-motion-permission", result.motionPermission);

    for (const [name, value] of Object.entries(result.tests)) {
      scope.setTag(`support-${name}`, String(value ?? "untested"));
    }

    scope.setContext("iproov-support", {
      ...result,
      failedTests: result.failedTests.join(",") || "none",
    });
    scope.setContext("verification", {
      verificationId: verificationId || "unknown",
      sessionTokenPresent: !!sessionToken,
    });
    // Never "browser" — that's a Sentry context key; overwriting it breaks Discover's
    // browser.name filter. Same defect VEX-7628 fixed by renaming "device" (VEX-7628).
    scope.setContext("browserSnapshot", {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
    });

    if (result.ran) {
      Sentry.captureMessage(`iProov Unsupported (pre-flight): ${primary}`, 'error');
    } else {
      Sentry.captureMessage(
        `iProov Support Check Skipped: ${result.failOpenReason ?? "unknown"}`,
        'warning'
      );
    }
  });
}

/**
 * Track iProov permission denied event.
 *
 * iProov denials come in two flavors we must distinguish: `camera` (getUserMedia)
 * and `motion` (iOS DeviceMotion/orientation). Tagged as `permission-type` and
 * reflected in the message so the two form distinct Sentry issues and the
 * camera-vs-motion split is queryable (VEX-7599). The camera message text is kept
 * stable so it continues grouping with the existing high-volume issue.
 *
 * `feedback` is iProov's own code for the denial and is what `permissionType` is now
 * derived from; both are recorded so a disagreement between the code and the
 * caller's fallback is visible rather than silent (VEX-7631).
 *
 * @param verificationId - Current verification ID
 * @param sessionToken - Session token (checked for presence)
 * @param permissionType - Which permission was denied ("camera" | "motion")
 * @param feedback - iProov feedback code from reasons[0], where supplied
 * @param eventDetail - Full event detail object
 */
export function trackIProovPermissionDenied(
  verificationId: string | null,
  sessionToken: string | null,
  permissionType: "camera" | "motion" = "camera",
  feedback?: string,
  eventDetail?: unknown
): void {
  Sentry.withScope((scope) => {
    tagMotionSupport(scope);
    scope.setTag("liveness-event", "permission_denied");
    scope.setTag("permission-type", permissionType);
    scope.setTag("liveness-feedback", feedback || "none");
    scope.setContext("verification", {
      verificationId: verificationId || "unknown",
      sessionTokenPresent: !!sessionToken,
    });
    if (eventDetail) {
      scope.setContext("iproov-event", flattenIProovDetail(eventDetail));
    }
    const label = permissionType === "motion" ? "Device motion access denied" : "Camera access denied";
    Sentry.captureMessage(`iProov Permission Denied: ${label}`, 'error');
  });
}

/**
 * Track an iProov permission-recovery reload — the user tapped "Reload" on the
 * camera/motion-denied recovery screen so the host (top) page reloads and the
 * browser re-prompts (mainly Safari iOS, whose permissions are scoped to the
 * top-level document load). Captured as a message (not just a breadcrumb) because
 * the host reload tears the document down immediately after; pair with
 * flushSentry() so it isn't dropped.
 *
 * @param verificationId - Current verification ID
 * @param permissionType - Which permission the user is recovering ("camera" | "motion")
 */
export function trackIProovReloadRequest(
  verificationId: string | null,
  permissionType: "camera" | "motion" = "camera"
): void {
  Sentry.withScope((scope) => {
    scope.setTag("liveness-event", "reload_request");
    scope.setTag("permission-type", permissionType);
    scope.setContext("verification", {
      verificationId: verificationId || "unknown",
    });
    Sentry.captureMessage("iProov Reload Request: permission recovery (host page reload)", "info");
  });
}

/**
 * Set user context for verification session
 *
 * @param verificationId - Verification session ID
 * @param sessionTokenPrefix - First few chars of session token (for correlation without exposing full token)
 */
export function setVerificationUser(
  verificationId: string,
  sessionTokenPrefix?: string
): void {
  Sentry.setUser({
    id: verificationId,
    ...(sessionTokenPrefix && { sessionTokenPrefix }),
  });
}

/**
 * Tag the session with the IVA VerificationAttempt id. IVA passes it to the SDK
 * as `externalReferenceId` (see IVA's *_websdk_controller:
 * `externalReferenceId: this.trackingIdValue`, which renders `attempt.uuid`), but
 * we surface it in Sentry under the clearer `verification_attempt_id` key.
 *
 * This is the stable key back to the IVA attempt: Sentry `user.id` is the athena
 * verification uuid, and a NEW verification is minted on every camera/motion
 * permission recovery reload, so it changes across a reload while the attempt id
 * does not. Tagging it lets us (a) group all of a user's reload events + the
 * eventual outcome under one key, and (b) join Sentry → the IVA attempt directly
 * (athena doesn't expose it as a queryable column — it's encrypted in the vendor
 * `vendorData`). Set as a searchable TAG, not context, so it's filterable and
 * survives the per-event `withScope` calls that overwrite the "verification"
 * context. Shared by the Legal ID and Face Verification flows.
 *
 * @param verificationAttemptId - IVA VerificationAttempt uuid (the SDK's externalReferenceId)
 */
export function setVerificationAttemptId(verificationAttemptId: string): void {
  Sentry.setTag("verification_attempt_id", verificationAttemptId);
}

/**
 * Track API error with context
 *
 * @param error - Error object or unknown
 * @param endpoint - API endpoint (e.g., 'POST /v1/verifications/faces')
 * @param context - Additional context (verificationId, flow, etc.)
 */
export function trackAPIError(
  error: unknown,
  endpoint: string,
  context: {
    flow?: string;
    verificationId?: string;
    httpStatus?: number;
    errorData?: unknown;
    sessionTokenPresent?: boolean;
  } = {}
): void {
  Sentry.withScope((scope) => {
    scope.setTag("api-endpoint", endpoint);
    if (context.httpStatus) {
      scope.setTag("http-status", context.httpStatus);
    }
    scope.setContext("api", {
      endpoint,
      ...context,
    });
    Sentry.captureException(error);
  });
}

/**
 * Track initialization/flow error
 *
 * @param error - Error object or unknown
 * @param errorType - Type of error (e.g., 'initialization_failed', 'polling_failed')
 * @param flow - Verification flow type
 * @param context - Additional context
 */
export function trackFlowError(
  error: unknown,
  errorType: string,
  flow: string,
  context: Record<string, unknown> = {}
): void {
  Sentry.withScope((scope) => {
    scope.setTag("error_type", errorType || "unknown");
    scope.setTag("flow", flow || "unknown");
    if (context?.failedApi) {
      scope.setTag("failed_api", context.failedApi as string);
    }
    scope.setContext("api", context ?? {});
    Sentry.captureException(error);
  });
}

/**
 * Why the analytics pipeline is considered broken for this launch:
 *  - `never_initialized` — an event was emitted but `initAnalytics()` never ran.
 *  - `first_send_failed` — the first real event couldn't be delivered (network
 *    error or non-2xx — endpoint down, CORS, schema rejection, etc). Context
 *    carries `httpStatus` + `detail` (the server's problem+json body — usually
 *    names the exact schema violation) for a non-2xx, or `networkErrorMessage`
 *    (the browser's own fetch-rejection message) for a network-level failure.
 *  - `missing_application_id` — no `applicationId` configured; every event
 *    this launch will be schema-rejected (`format:"uuid"`, required).
 *  - `init_failed` — `initAnalytics()` itself threw (e.g. `crypto.randomUUID`
 *    unavailable); analytics is unavailable this launch. Caught at the call site
 *    so it can't block the verification flow.
 */
export type AnalyticsSetupErrorReason = "never_initialized" | "first_send_failed" | "missing_application_id" | "init_failed";

/**
 * Track an analytics initialization health failure for this launch — see
 * {@link AnalyticsSetupErrorReason} for what each reason means.
 *
 * ONE signal per launch, not per dropped event — callers must gate this behind
 * a per-launch guard (`analyticsClient.ts`'s `healthReported`).
 *
 * @param reason - which check failed
 * @param context - launch detail available at the time (flow, elementName, httpStatus, etc.)
 */
export function trackAnalyticsSetupError(
  reason: AnalyticsSetupErrorReason,
  context: Record<string, unknown> = {}
): void {
  Sentry.withScope((scope) => {
    scope.setTag("analytics_setup_error", reason);
    scope.setContext("analytics_setup", context);
    // Without this, every first_send_failed — a 400 schema mismatch, a 5xx outage,
    // and a CORS/network block — collapses into one Sentry issue with an identical
    // title, so the issue list alone can't tell you which one you're looking at.
    // Fingerprint (and title) on the concrete cause instead; the full detail/error
    // text still lands in "analytics_setup" context above for the specific event.
    const cause =
      context.httpStatus ? `HTTP ${context.httpStatus}`
      : context.networkErrorMessage ? `network: ${context.networkErrorMessage}`
      : undefined;
    scope.setFingerprint(["analytics-setup-error", reason, String(cause ?? "")]);
    Sentry.captureMessage(
      `Analytics initialization error: ${reason}${cause ? ` (${cause})` : ""} — Issue with emitting events in this session`,
      "warning",
    );
  });
}

/**
 * Track an SSN card capture failure (Regula `<document-reader>`).
 *
 * Captures *why* a capture failed so the varied scenarios are distinguishable in
 * Sentry: the mapped terminal error code, the capture `feedback` detail behind a
 * generic code (capture_timeout / capture_quality_failed / no_capture_result), the
 * raw Regula reason string, the phase (init vs process), and the Regula status code.
 *
 * @param errorCode - Mapped terminal SsnCardErrorCode
 * @param detail - Diagnostic detail (feedback, raw Regula reason, phase, status)
 */
export function trackSsnCardCaptureError(
  errorCode: string,
  detail: {
    feedback?: string;
    reason?: string;
    phase?: "init" | "process" | "accepted_after_retakes";
    status?: number;
    side?: "front" | "back";
  } = {}
): void {
  Sentry.withScope((scope) => {
    scope.setTag("flow", "ssn-card");
    scope.setTag("ssn_capture_error", errorCode || "unknown");
    scope.setTag("ssn_capture_feedback", detail.feedback || "none");
    scope.setTag("ssn_capture_reason", detail.reason || "none");
    scope.setTag("ssn_capture_phase", detail.phase || "process");
    scope.setContext("ssn_capture", { ...detail, errorCode });
    // Put the specific discriminator in the message so distinct triggers form distinct
    // Sentry issues (e.g. capture_init_failed (WASM_LICENSE) vs (WASM_ERROR)), not one
    // generic bucket. Prefer the capture `feedback`, else the raw Regula `reason`.
    const detailSuffix = detail.feedback ?? detail.reason;
    Sentry.captureMessage(
      `SSN card capture error: ${errorCode}${detailSuffix ? ` (${detailSuffix})` : ""}`,
      "warning",
    );
  });
}

/**
 * Capture a device snapshot at Legal ID capture start and attach it as
 * persistent Sentry context so it rides along with any later event / Session
 * Replay for this session (incl. the eventual cancel/error). Fires even when
 * the user later abandons, which the result payload cannot capture.
 *
 * Non-blocking and never throws; resolves with the collected info (or null).
 *
 * @param verificationId - Current verification/inspection ID
 */
export async function trackVeriffDeviceContext(
  verificationId: string | null,
): Promise<DeviceInfo | null> {
  try {
    const info = await collectDeviceInfo();

    // VEX-7628: NOT setContext("device", …) — that name overwrites Sentry's own
    // built-in device context, which is why affected events showed only our keys.
    Sentry.setContext("deviceSnapshot", info as unknown as Record<string, unknown>);

    // Custom context keys are not indexed, so nothing inside setContext can be
    // searched or grouped in Discover — the media fields had to be read by opening
    // events one at a time. Promote the low-cardinality ones to tags so denial
    // cohorts are aggregatable. Names carry the `frame` prefix because these are
    // frame-scoped and were previously misread as the member's site-level grant.
    Sentry.setTag("frame-camera-permission", info.cameraPermission ?? "unknown");
    Sentry.setTag("frame-mic-permission", info.micPermission ?? "unknown");
    Sentry.setTag("video-input-count", String(info.videoInputCount ?? "unknown"));
    Sentry.setTag("audio-input-count", String(info.audioInputCount ?? "unknown"));
    Sentry.setTag("video-input-labeled", String(info.videoInputLabeled ?? "unknown"));
    Sentry.setTag("audio-input-labeled", String(info.audioInputLabeled ?? "unknown"));

    Sentry.addBreadcrumb({
      category: "veriff-event",
      message: "Device snapshot",
      level: "info",
      data: { verificationId: verificationId || "unknown", ...info },
    });
    return info;
  } catch {
    return null;
  }
}

/**
 * Track a Legal ID (Veriff) cancellation. Mirrors trackIProovCanceled; device
 * context set by trackVeriffDeviceContext is already attached to the scope.
 *
 * @param verificationId - Current verification/inspection ID
 */
export function trackVeriffCanceled(verificationId: string | null): void {
  Sentry.withScope((scope) => {
    scope.setTag("legalid-event", "canceled");
    scope.setContext("verification", {
      verificationId: verificationId || "unknown",
    });
    Sentry.captureMessage("Veriff Canceled: User canceled document capture", "info");
  });
}


/**
 * Best-effort flush of queued Sentry events. Used before a host-page reload
 * (Veriff RELOAD_REQUEST) so the just-captured reload metric isn't dropped when
 * the document is torn down. Never throws; resolves regardless of outcome.
 *
 * @param timeoutMs - Max time to wait for the transport to drain
 */
export async function flushSentry(timeoutMs = 2000): Promise<void> {
  try {
    await Sentry.flush(timeoutMs);
  } catch {
    // Ignore — the caller reloads regardless of flush success.
  }
}
